import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import * as React from "react";

/** Matches product listing TooltipContent (popover surface, light border, no arrow). */
export const appTooltipContentClassName =
  "z-[250] max-w-xs rounded-md border border-border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2";

export type AppTooltipProps = {
  /** Tooltip text or rich content */
  label: React.ReactNode;
  children: React.ReactElement;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  sideOffset?: number;
  delayDuration?: number;
  disabled?: boolean;
  contentClassName?: string;
};

/**
 * Shared tooltip — use anywhere inside `TooltipProvider` (root App already wraps it).
 * Styling matches product listing tooltips (bg-popover / border).
 *
 * @example
 * <AppTooltip label="Delete batch">
 *   <Button variant="ghost" size="icon"><Trash2 /></Button>
 * </AppTooltip>
 */
export function AppTooltip({
  label,
  children,
  side = "top",
  align = "center",
  sideOffset = 4,
  delayDuration = 200,
  disabled = false,
  contentClassName,
}: AppTooltipProps) {
  if (disabled || label == null || label === "") {
    return children;
  }

  const childDisabled =
    React.isValidElement(children) &&
    typeof children.props === "object" &&
    children.props !== null &&
    "disabled" in children.props &&
    Boolean((children.props as { disabled?: boolean }).disabled);

  const trigger = childDisabled ? <span className="inline-flex">{children}</span> : children;

  return (
    <Tooltip delayDuration={delayDuration}>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent
        side={side}
        align={align}
        sideOffset={sideOffset}
        className={cn(appTooltipContentClassName, contentClassName)}
      >
        {label}
      </TooltipContent>
    </Tooltip>
  );
}
